import { useState, useEffect, useMemo, useRef, ChangeEvent } from 'react';
import styled from 'styled-components';
import H3 from '@sdk/components/ui/typography/H3';
import gettext from '@sdk/lang/gettext';
import Text16 from '@sdk/components/ui/typography/Text16';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import TextField from '@sdk/components/forms/TextField';
import ProgressButton from '@sdk/components/ui/buttons/ProgressButton';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import useSlingApi from '@sdk/api/useSlingApi';
import useAnalytics from '@sdk/analytics/useAnalytics';
import { AnalyticsEvents } from '@sdk/analytics/types';
import Clickable from '@sdk/components/primitives/Clickable';
import Text14 from '@sdk/components/ui/typography/Text14';
import { PositionType } from '@sdk/positions/types/PositionType';
import { LocationType } from '@sdk/locations/types/LocationType';
import PickerField from '@sdk/components/forms/PickerField';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import { GENERIC_SERVER_ERROR } from '@sdk/login/constants';
import FieldError from '@sdk/components/forms/components/FieldError';
import Switcher from '@sdk/components/forms/Switcher';
import { INPUT_HEIGHT } from '@sdk/components/forms/constants';
import validateEmail from '@sdk/utils/validation/validateEmail';
import useAuth from '@sdk/authentication/useAuth';

import EmailField from '@web/onboarding/components/EmailField';
import getDefaultEmployees, {
  FormEmployee,
  getEmployeeForIndex,
} from '@web/onboarding/helpers/getDefaultEmployees';

const Container = styled(Column)`
  max-width: 700px;
  flex-grow: 1;
  flex-shrink: 1;
`;

const Button = styled(ProgressButton)`
  flex-grow: 0.5;
  flex-basis: 0;
  max-width: 205px;
`;

const ButtonContainer = styled(Row)`
  flex: 1;
`;

const SkipButton = styled(Clickable)`
  opacity: 0.65;
`;

const FieldContainer = styled(Column)`
  flex: 1;
`;

type Props = {
  maybeCreateFirstLocation: () => Promise<LocationType | undefined>;
  onSuccess: () => void;
  onSkip: () => void;
  addPersonaCollectionUser: (user: { user: any }) => void;
  positions: PositionType[];
};

export const EmployeesStep = ({
  maybeCreateFirstLocation,
  onSuccess,
  onSkip,
  addPersonaCollectionUser,
  positions,
}: Props) => {
  const { currentUser } = useAuth();
  const api = useSlingApi();
  const [employees, setEmployees] = useState<FormEmployee[]>();
  const [shouldSendInvite, setShouldSendInvite] = useState(false);
  const [hasSuggestedInvite, setHasSuggestedInvite] = useState(false);
  const { track } = useAnalytics({
    userId: currentUser.id,
    email: currentUser.email,
    orgId: currentUser.org.id,
    toastRestaurantGuids: currentUser.org.toastRestaurantGuids,
  });
  const focusFieldRef = useRef(null);
  const wasFocusedRef = useRef(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (focusFieldRef.current && !wasFocusedRef.current) {
      focusFieldRef.current.focus();
      wasFocusedRef.current = true;
    }
  });

  const positionsDict = useMemo(
    () => positions && positions?.map((p) => ({ value: p.id, label: p.name })),
    [positions]
  );

  useEffect(() => {
    if (!employees) {
      const updatedPositions = getDefaultEmployees(positions);

      setEmployees(updatedPositions);
    }
  }, [employees, positions]);

  useEffect(() => {
    const hasEmail = employees?.some(({ email }) => email.trim());
    if (!hasSuggestedInvite && hasEmail) {
      setHasSuggestedInvite(true);
      setShouldSendInvite(true);
    }
  }, [employees, hasSuggestedInvite]);

  const setEmployeeName = (index: number, name: string): void => {
    const updatedEmployees = [...employees];
    if (updatedEmployees[index]) {
      updatedEmployees[index].fullName = name;
      setEmployees(updatedEmployees);
    }

    if (index === employees.length - 1 && name) {
      addEmployee();
    }
  };

  const setEmployeeEmail = (index: number, email: string): void => {
    const updatedEmployees = [...employees];
    if (updatedEmployees[index]) {
      updatedEmployees[index].email = email;
      setEmployees(updatedEmployees);
    }
  };

  const setEmployeePosition = (index: number, id: number): void => {
    const updatedEmployees = [...employees];
    if (updatedEmployees[index]) {
      updatedEmployees[index].position = { id };
      setEmployees(updatedEmployees);
    }
  };

  const addEmployee = (): void => {
    const newEmployee = getEmployeeForIndex(positions.length, positions);
    setEmployees([...employees, newEmployee]);
  };

  const isEmployeeNameInvalid = (index: number): boolean | string => {
    const employee = employees?.[index];
    if (!employee) {
      return false;
    }

    const emailTrimmed = employee.email?.trim?.() || '';

    if (!employee.fullName && emailTrimmed) {
      return gettext('Please provide employee name');
    }

    return false;
  };

  const isEmailInvalid = (index: number): boolean | string => {
    const email = employees?.[index]?.email;
    const emailTrimmed = email.trim?.() || '';

    if (!email || !emailTrimmed) {
      return false;
    }

    const firstEmailIndex = employees.findIndex((e) => e.email === email);
    if (firstEmailIndex < index || emailTrimmed === currentUser.email) {
      return gettext('This email address is already taken');
    }

    if (!validateEmail(email.trim?.())) {
      return gettext('This email address is invalid');
    }

    return false;
  };

  const validate = (): boolean => {
    return (
      !employees?.some((_, i) => isEmailInvalid(i)) &&
      !employees?.some((_, i) => isEmployeeNameInvalid(i))
    );
  };

  const submit = async (): Promise<void> => {
    setIsSubmitted(true);
    setServerErrors([]);

    if (!validate()) {
      throw 'Form invalid';
    }

    const location = await maybeCreateFirstLocation();
    const employeesToSave = employees
      .map((e) => {
        const [name, ...last] = e.fullName.trim().split(' ');
        return {
          groups: [e.position, { id: location?.id }],
          name,
          lastname: last.join(' '),
          email: e.email,
        };
      })
      .filter((e) => e.name || e.lastname);

    try {
      if (employeesToSave.length) {
        const savedEmployeesResponse = await api.users.addNewUsers(
          shouldSendInvite,
          employeesToSave
        );
        const createdUsers = savedEmployeesResponse.data?.users || [];

        if (createdUsers.length) {
          createdUsers.forEach((user) => addPersonaCollectionUser({ user }));
          track(AnalyticsEvents.orgSetupAddedUsers, savedEmployeesResponse.data.users.length);
        }
      }

      onSuccess();
    } catch (e) {
      if (e?.data?.errors) {
        setServerErrors(e.data.errors);
        return;
      }

      if (e?.data?.message) {
        setServerErrors([e.data.message]);
        return;
      }

      setServerErrors([GENERIC_SERVER_ERROR]);
    }
  };

  if (!employees) {
    return null;
  }

  return (
    <Container>
      <H3 mTop={20} mBottom={10}>
        {gettext('Set up your schedule')}
      </H3>
      <Text16 themeColor={ThemeColorType.gray}>
        {gettext('Finally, add a few employees so you have someone to schedule.')}
      </Text16>
      <Text16 themeColor={ThemeColorType.gray}>
        {gettext('Employees will not be invited automatically.')}
      </Text16>
      <Row mTop={15} />
      {employees.map((employee, i) => (
        <Row mTop={15} key={i}>
          <TextField
            ref={i === 0 ? focusFieldRef : null}
            value={employee.fullName}
            placeholder={gettext('Full name')}
            label={i === 0 ? gettext('Full name') : null}
            onChange={(e): void => setEmployeeName(i, e.currentTarget.value)}
            height={INPUT_HEIGHT}
            invalid={isSubmitted && Boolean(isEmployeeNameInvalid(i))}
            error={isSubmitted ? String(isEmployeeNameInvalid(i)) : ''}
          />
          <FieldContainer mLeft={10}>
            <EmailField
              skipLabel={i !== 0}
              value={employee.email}
              onChange={(e: ChangeEvent<HTMLInputElement>): void =>
                setEmployeeEmail(i, e.currentTarget.value)
              }
              invalid={isSubmitted && Boolean(isEmailInvalid(i))}
              error={String((isSubmitted && isEmailInvalid(i)) || '')}
            />
          </FieldContainer>
          {!!positionsDict.length && (
            <FieldContainer mLeft={10}>
              <PickerField
                label={i === 0 ? gettext('Position') : null}
                placeholder={gettext('Select position')}
                options={positionsDict}
                value={employee.position?.id}
                onChange={(value: number): void => setEmployeePosition(i, value)}
                height={INPUT_HEIGHT}
              />
            </FieldContainer>
          )}
        </Row>
      ))}

      {!!serverErrors.length && (
        <Column mTop={15}>
          {serverErrors.map((error, i) => (
            <FieldError invalid={true} error={error} key={i} />
          ))}
        </Column>
      )}

      <Row mTop={40} pBottom={40} justifyContent="space-between" alignItems="center">
        <Row pRight={15} alignItems="center">
          <Text14 color={ThemeColorType.gray} semiBold>
            {gettext('Invite employees')}
          </Text14>
          <Row pLeft={15}>
            <Switcher small value={shouldSendInvite} onChange={setShouldSendInvite} />
          </Row>
        </Row>

        <ButtonContainer justifyContent="flex-end" alignItems="center">
          <Row mRight={20}>
            <SkipButton onClick={onSkip}>
              <Text14 themeColor={ThemeColorType.gray} underline>
                {gettext("I'll do this later")}
              </Text14>
            </SkipButton>
          </Row>

          <Button buttonType={ButtonType.primary} onClick={submit} height={INPUT_HEIGHT}>
            {gettext('Start scheduling')}
          </Button>
        </ButtonContainer>
      </Row>
    </Container>
  );
};
